import { ref, computed, readonly } from 'vue'
import {
  ROW_HEIGHT, SNAP_MINUTES, MIN_EVENT_MINUTES,
} from 'composables/useCalendarGeometry.js'

// One pointer-event state machine behind all four grid gestures:
//
//   create        drag on empty grid  → a new event with a duration
//   move          drag a chip's body  → same duration, new start (and day)
//   resize-start  drag a chip's top   → new start, end fixed
//   resize-end    drag a chip's bottom→ new end, start fixed
//
// Pointer events (not mouse events) so this works on touch and stylus, and
// setPointerCapture so a fast drag that leaves the column still tracks.
//
// The composable owns geometry and gesture state only. It never talks to the
// store or the API — the host view commits through the callbacks it passes in.

export const DRAG_THRESHOLD_PX = 4 // below this a gesture is a click, not a drag
export const EDGE_ZONE_PX      = 6 // hit zone at a chip's top/bottom for resizing
const AUTOSCROLL_ZONE_PX       = 40
const AUTOSCROLL_MAX_PX        = 14

export function useGridDrag({ dayColumns, scrollEl, onCommit, onCreate }) {
  // dayColumns: () => [{ dayKey, date, el }]   resolved lazily at drag start
  // scrollEl:   () => HTMLElement | null       the scrolling grid container

  const drag = ref(null)     // null when idle
  const isDragging = ref(false)

  let _autoscrollFrame = null
  let _pointerClientY  = 0
  let _pointerClientX  = 0
  // A completed drag still fires a click on release. isDragging is already back
  // to false by then, so the views need a short-lived flag instead to avoid
  // opening the popover or the create form on top of the drag they just made.
  let _lastDragEndAt = 0

  const active = computed(() => isDragging.value && !!drag.value)

  // ── Geometry ───────────────────────────────────────────────────────────────

  const snap = (minutes) =>
    Math.max(0, Math.min(24 * 60, Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES))

  function minutesFromClientY(clientY, columnEl) {
    const rect = columnEl.getBoundingClientRect()
    return snap(((clientY - rect.top) / ROW_HEIGHT) * 60)
  }

  // Which day column is under the pointer, so a drag can cross days.
  function columnAtClientX(clientX) {
    const columns = dayColumns() ?? []
    for (const column of columns) {
      if (!column.el) continue
      const rect = column.el.getBoundingClientRect()
      if (clientX >= rect.left && clientX <= rect.right) return column
    }
    // Outside every column: clamp to the nearest edge rather than losing the drag.
    if (!columns.length) return null
    const first = columns[0]
    const last  = columns[columns.length - 1]
    if (!first.el || !last.el) return null
    return clientX < first.el.getBoundingClientRect().left ? first : last
  }

  // ── Gesture start ──────────────────────────────────────────────────────────

  /**
   * @param {PointerEvent} event
   * @param {'create'|'move'|'resize-start'|'resize-end'} mode
   * @param {object} context { booking, segment, column }
   */
  function start(event, mode, context = {}) {
    if (event.button != null && event.button !== 0) return // left button / touch only
    const column = context.column ?? columnAtClientX(event.clientX)
    if (!column?.el) return

    const pointerMinutes = minutesFromClientY(event.clientY, column.el)

    drag.value = {
      mode,
      booking:      context.booking ?? null,
      originColumn: column,
      column,
      // Where the gesture began, used for the movement threshold.
      originX: event.clientX,
      originY: event.clientY,
      pointerMinutes,
      // Working values, updated on every move.
      startMinutes: context.segment?.startMinutes ?? pointerMinutes,
      endMinutes:   context.segment?.endMinutes   ?? pointerMinutes + SNAP_MINUTES,
      // For 'move', the pointer's offset inside the chip so it does not jump.
      grabOffset:   pointerMinutes - (context.segment?.startMinutes ?? pointerMinutes),
      durationMinutes:
        (context.segment?.endMinutes ?? pointerMinutes + SNAP_MINUTES) -
        (context.segment?.startMinutes ?? pointerMinutes),
    }

    isDragging.value = false
    _pointerClientX = event.clientX
    _pointerClientY = event.clientY

    // Suppress the browser's own gestures. A mousedown on a chip (which holds
    // text) otherwise starts a native text selection or an HTML5 element drag,
    // and the browser then fires pointercancel and takes the pointer stream
    // away from us mid-gesture. jsdom models none of this, so it only shows up
    // in a real browser.
    event.preventDefault?.()
    document.body.style.userSelect = 'none'

    // Deliberately no setPointerCapture: the chip being dragged gets dimmed and
    // re-rendered, and capturing on an element that then changes underneath us
    // caused the browser to fire pointercancel and kill the drag. The window
    // listeners below already keep tracking once the pointer leaves the column.
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerCancel)
    // A native drag starting mid-gesture would steal the pointer; refuse it.
    window.addEventListener('dragstart', preventNative)
    window.addEventListener('selectstart', preventNative)
  }

  function preventNative(event) {
    event.preventDefault()
  }

  // Commit whatever the gesture had reached rather than silently dropping it:
  // a cancel that arrives after a real drag should not look like nothing
  // happened.
  function onPointerCancel() {
    if (isDragging.value) onPointerUp()
    else teardown()
  }

  // ── Gesture update ─────────────────────────────────────────────────────────

  function onPointerMove(event) {
    if (!drag.value) return
    _pointerClientX = event.clientX
    _pointerClientY = event.clientY

    if (!isDragging.value) {
      const moved = Math.hypot(event.clientX - drag.value.originX, event.clientY - drag.value.originY)
      if (moved < DRAG_THRESHOLD_PX) return // still a click
      isDragging.value = true
      startAutoscroll()
    }

    event.preventDefault()
    applyPointer()
  }

  function applyPointer() {
    const state = drag.value
    if (!state) return

    const column = columnAtClientX(_pointerClientX) ?? state.column
    const minutes = minutesFromClientY(_pointerClientY, column.el)

    if (state.mode === 'create') {
      // Anchored at wherever the drag began; dragging upward is allowed.
      const anchor = state.pointerMinutes
      const low  = Math.min(anchor, minutes)
      const high = Math.max(anchor, minutes)
      state.startMinutes = low
      state.endMinutes   = Math.max(high, low + MIN_EVENT_MINUTES)
      state.column       = column
      return
    }

    if (state.mode === 'move') {
      const start = Math.max(0, Math.min(24 * 60 - state.durationMinutes, minutes - state.grabOffset))
      state.startMinutes = start
      state.endMinutes   = start + state.durationMinutes
      state.column       = column
      return
    }

    if (state.mode === 'resize-start') {
      // Resizing never changes the day, only the edge.
      state.startMinutes = Math.min(minutes, state.endMinutes - MIN_EVENT_MINUTES)
      return
    }

    if (state.mode === 'resize-end') {
      state.endMinutes = Math.max(minutes, state.startMinutes + MIN_EVENT_MINUTES)
    }
  }

  // ── Autoscroll ─────────────────────────────────────────────────────────────

  function startAutoscroll() {
    if (_autoscrollFrame) return
    const step = () => {
      const el = scrollEl?.()
      if (el && isDragging.value) {
        const rect = el.getBoundingClientRect()
        const fromTop    = _pointerClientY - rect.top
        const fromBottom = rect.bottom - _pointerClientY
        let delta = 0
        if (fromTop < AUTOSCROLL_ZONE_PX) {
          delta = -AUTOSCROLL_MAX_PX * (1 - Math.max(fromTop, 0) / AUTOSCROLL_ZONE_PX)
        } else if (fromBottom < AUTOSCROLL_ZONE_PX) {
          delta = AUTOSCROLL_MAX_PX * (1 - Math.max(fromBottom, 0) / AUTOSCROLL_ZONE_PX)
        }
        if (delta !== 0) {
          el.scrollTop += delta
          applyPointer() // the grid moved under the pointer, so recompute
        }
      }
      _autoscrollFrame = isDragging.value ? requestAnimationFrame(step) : null
    }
    _autoscrollFrame = requestAnimationFrame(step)
  }

  // ── Gesture end ────────────────────────────────────────────────────────────

  function onPointerUp() {
    const state = drag.value
    const dragged = isDragging.value
    teardown()
    if (!state || !dragged) return // a click; the view's own @click handles it

    _lastDragEndAt = Date.now()

    const payload = {
      booking:      state.booking,
      column:       state.column,
      startMinutes: state.startMinutes,
      endMinutes:   Math.max(state.endMinutes, state.startMinutes + MIN_EVENT_MINUTES),
      mode:         state.mode,
    }

    if (state.mode === 'create') onCreate?.(payload)
    else onCommit?.(payload)
  }

  function cancel() {
    teardown()
  }

  function teardown() {
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerup', onPointerUp)
    window.removeEventListener('pointercancel', onPointerCancel)
    window.removeEventListener('dragstart', preventNative)
    window.removeEventListener('selectstart', preventNative)
    if (_autoscrollFrame) cancelAnimationFrame(_autoscrollFrame)
    _autoscrollFrame = null
    document.body.style.userSelect = ''
    drag.value = null
    isDragging.value = false
  }

  // ── Ghost chip ─────────────────────────────────────────────────────────────

  // The preview the view renders while a gesture is live. Returns null unless
  // the ghost belongs to the day column being asked about.
  function ghostFor(dayKey) {
    const state = drag.value
    if (!active.value || !state || state.column?.dayKey !== dayKey) return null
    return {
      mode:         state.mode,
      booking:      state.booking,
      startMinutes: state.startMinutes,
      endMinutes:   state.endMinutes,
      top:          (state.startMinutes / 60) * ROW_HEIGHT,
      height:       Math.max(((state.endMinutes - state.startMinutes) / 60) * ROW_HEIGHT, 12),
    }
  }

  // While moving or resizing, the chip being dragged is drawn as the ghost, so
  // the original is hidden to avoid rendering it twice.
  function isBeingDragged(bookingId) {
    return active.value && drag.value?.mode !== 'create' && drag.value?.booking?.id === bookingId
  }

  // True for a moment after a drag ends, so the synthetic click can be ignored.
  const justDragged = () => Date.now() - _lastDragEndAt < 250

  return {
    drag: readonly(drag),
    isDragging: readonly(isDragging),
    active,
    start,
    cancel,
    ghostFor,
    isBeingDragged,
    justDragged,
  }
}
